import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export default function Login(): React.JSX.Element {
  const { login, isAuthenticated, isLoading, error } = useAuth();
  const redirectStarted = useRef(false);
  const [redirectError, setRedirectError] = useState<string | null>(null);
  const loginError = redirectError ?? error;

  const startLogin = useCallback(() => {
    redirectStarted.current = true;
    setRedirectError(null);
    void login().catch((reason) => {
      redirectStarted.current = false;
      setRedirectError(reason instanceof Error ? reason.message : 'Unable to start secure sign-in.');
    });
  }, [login]);

  useEffect(() => {
    if (!isLoading && !isAuthenticated && !error && !redirectError && !redirectStarted.current) startLogin();
  }, [error, isAuthenticated, isLoading, redirectError, startLogin]);

  if (!isLoading && isAuthenticated) return <Navigate to="/dashboard" replace />;

  return (
    <main className="mx-auto flex max-w-md flex-col px-6 py-20 text-center">
      <div className="rounded-xl border border-slate-200 bg-white p-8 shadow-lg">
        <h1 className="text-2xl font-bold text-mcc-darkest">
          {loginError ? 'Unable to sign in' : 'Signing you in…'}
        </h1>
        <p className="mt-2 text-sm text-slate-600">
          Redirecting to the secure My Connective Care sign-in.
        </p>
        {loginError && (
          <>
            <p className="mt-4 text-sm text-red-700" role="alert">{loginError}</p>
            <button
              type="button"
              onClick={startLogin}
              className="mt-6 w-full rounded-md bg-mcc-neutral px-4 py-2.5 font-semibold text-white"
            >
              Try again
            </button>
          </>
        )}
      </div>
    </main>
  );
}
